import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import PreviewIcon from '@mui/icons-material/Preview'
import SendIcon from '@mui/icons-material/Send'
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone'
import ComputerIcon from '@mui/icons-material/Computer'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { supabase, functionAuthHeaders } from '../../config/supabase'
import { ENV } from '../../config/env'

interface EmailTemplate {
  id: string
  name: string
  slug: string
  description?: string
  subject: string
  html_body: string
  plain_text_body?: string
  variables: Array<{ key: string; label: string; description?: string }>
  is_active: boolean
  is_default: boolean
}

interface Variable {
  key: string
  label: string
  description?: string
}

const DEFAULT_VARIABLES: Variable[] = [
  { key: 'virksomhedsnavn', label: 'Virksomhedsnavn', description: 'Navnet på bedemanden eller kæden (fra profil)' },
  { key: 'email', label: 'Email', description: 'Email adressen (fra profil)' },
  { key: 'link', label: 'Link', description: 'Det unikke link til udfyldelse' },
  { key: 'profil_link', label: 'Profil link', description: 'Link til virksomhedens profil på find-bedemand.dk' },
  { key: 'adresse', label: 'Adresse', description: 'Virksomhedens adresse (fra profil)' },
  { key: 'by', label: 'By', description: 'Virksomhedens by (fra profil)' },
  { key: 'telefon', label: 'Telefon', description: 'Telefonnummer (fra profil)' },
]

const PREVIEW_DATA: Record<string, string> = {
  virksomhedsnavn: 'Bedemand Riising',
  email: 'kontakt@riising.dk',
  link: 'https://admin.find-bedemand.dk/udfyld-services/abc123xyz',
  profil_link: 'https://find-bedemand.dk/bedemand/bedemand-riising/',
  adresse: 'Hovedgaden 123',
  by: 'København',
  telefon: '12 34 56 78',
}

export default function EmailTemplateEditor() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  
  const [tabValue, setTabValue] = useState(0)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop')
  const [testEmailOpen, setTestEmailOpen] = useState(false)
  const [testEmail, setTestEmail] = useState('')
  const [sendingTest, setSendingTest] = useState(false)

  useEffect(() => {
    fetchTemplates()
  }, [])

  const fetchTemplates = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .order('is_default', { ascending: false })
        .order('name')

      if (error) throw error
      setTemplates(data || [])
      
      // Auto-select default template
      const defaultTemplate = data?.find(t => t.is_default) || data?.[0]
      if (defaultTemplate) {
        setSelectedTemplate(defaultTemplate)
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!selectedTemplate) return

    try {
      setSaving(true)
      setError(null)

      console.log('Saving template:', selectedTemplate.id, selectedTemplate.name)

      const { data, error } = await supabase
        .from('email_templates')
        .update({
          name: selectedTemplate.name,
          subject: selectedTemplate.subject,
          html_body: selectedTemplate.html_body,
          plain_text_body: selectedTemplate.plain_text_body,
          description: selectedTemplate.description,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedTemplate.id)
        .select()

      console.log('Save response:', { data, error })

      if (error) {
        console.error('Supabase error:', error)
        throw error
      }

      if (!data || data.length === 0) {
        console.warn('No rows updated - possibly RLS issue')
        throw new Error('Kunne ikke gemme. Tjek at RLS policies er korrekt sat op i Supabase.')
      }

      setSuccess('✅ Email skabelon gemt!')
      setTimeout(() => setSuccess(null), 3000)
      
      await fetchTemplates()
    } catch (err: any) {
      console.error('Save error:', err)
      setError(err.message || 'Ukendt fejl ved gemning')
    } finally {
      setSaving(false)
    }
  }

  const replaceVariables = (text: string): string => {
    let result = text
    Object.entries(PREVIEW_DATA).forEach(([key, value]) => {
      result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value)
    })
    return result
  }

  const copyVariable = async (key: string) => {
    await navigator.clipboard.writeText(`{{${key}}}`)
    setSuccess(`✅ {{${key}}} kopieret!`)
    setTimeout(() => setSuccess(null), 2000)
  }

  const handleSendTestEmail = async () => {
    if (!testEmail || !selectedTemplate) return
    
    try {
      setSendingTest(true)
      setError(null)
      
      // Replace variables with preview data
      const subject = selectedTemplate.subject
        .replace(/\{\{virksomhedsnavn\}\}/g, 'Test Virksomhed')
        .replace(/\{\{email\}\}/g, testEmail)
        .replace(/\{\{link\}\}/g, 'https://example.com/test-link')
        .replace(/\{\{profil_link\}\}/g, 'https://find-bedemand.dk/bedemand/test-virksomhed/')
        .replace(/\{\{adresse\}\}/g, 'Testvej 123')
        .replace(/\{\{by\}\}/g, 'Testby')
        .replace(/\{\{telefon\}\}/g, '12 34 56 78')
      
      const html = selectedTemplate.html_body
        .replace(/\{\{virksomhedsnavn\}\}/g, 'Test Virksomhed')
        .replace(/\{\{email\}\}/g, testEmail)
        .replace(/\{\{link\}\}/g, 'https://example.com/test-link')
        .replace(/\{\{profil_link\}\}/g, 'https://find-bedemand.dk/bedemand/test-virksomhed/')
        .replace(/\{\{adresse\}\}/g, 'Testvej 123')
        .replace(/\{\{by\}\}/g, 'Testby')
        .replace(/\{\{telefon\}\}/g, '12 34 56 78')
      
      // Call Edge Function
      const response = await fetch(`${ENV.SUPABASE_URL}/functions/v1/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await functionAuthHeaders()),
        },
        body: JSON.stringify({
          to: testEmail,
          subject: `[TEST] ${subject}`,
          html: html,
        }),
      })
      
      const result = await response.json()
      console.log('Test email response:', result)
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to send test email')
      }
      
      setSuccess('✅ Test email sendt til ' + testEmail)
      setTestEmailOpen(false)
      setTestEmail('')
    } catch (err: any) {
      console.error('Test email error:', err)
      setError(err.message)
    } finally {
      setSendingTest(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Email Skabeloner
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Rediger email skabeloner med dynamiske variabler
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<PreviewIcon />}
            onClick={() => setPreviewOpen(true)}
          >
            Preview
          </Button>
          <Button
            variant="outlined"
            startIcon={<SendIcon />}
            onClick={() => setTestEmailOpen(true)}
          >
            Send Test
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || !selectedTemplate}
          >
            Gem Skabelon
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Left: Editor */}
        <Box sx={{ flex: 2 }}>
          {/* Template Selector */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Vælg skabelon</InputLabel>
            <Select
              value={selectedTemplate?.id || ''}
              label="Vælg skabelon"
              onChange={(e) => {
                const template = templates.find(t => t.id === e.target.value)
                setSelectedTemplate(template || null)
              }}
            >
              {templates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name}
                  {template.is_default && (
                    <Chip label="Standard" size="small" sx={{ ml: 1 }} />
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedTemplate && (
            <Card>
              <CardContent>
                {/* Template Name */}
                <TextField
                  fullWidth
                  label="Skabelon navn"
                  value={selectedTemplate.name}
                  onChange={(e) => setSelectedTemplate({
                    ...selectedTemplate,
                    name: e.target.value
                  })}
                  sx={{ mb: 3 }}
                />

                {/* Tabs */}
                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
                  <Tab label="Emnefelt" />
                  <Tab label="HTML Indhold" />
                  <Tab label="Plain Text" />
                </Tabs>

                {/* Subject */}
                {tabValue === 0 && (
                  <Box>
                    <TextField
                      fullWidth
                      label="Emnefelt"
                      value={selectedTemplate.subject}
                      onChange={(e) => setSelectedTemplate({
                        ...selectedTemplate,
                        subject: e.target.value
                      })}
                      placeholder="Opdater dine service-oplysninger på {{firma}}"
                      helperText="Brug {{variabel}} for dynamiske værdier"
                    />
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Preview:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {replaceVariables(selectedTemplate.subject)}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* HTML Body */}
                {tabValue === 1 && (
                  <TextField
                    fullWidth
                    multiline
                    rows={20}
                    label="HTML Indhold"
                    value={selectedTemplate.html_body}
                    onChange={(e) => setSelectedTemplate({
                      ...selectedTemplate,
                      html_body: e.target.value
                    })}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontFamily: 'monospace',
                        fontSize: '13px',
                      }
                    }}
                  />
                )}

                {/* Plain Text */}
                {tabValue === 2 && (
                  <TextField
                    fullWidth
                    multiline
                    rows={15}
                    label="Plain Text Version"
                    value={selectedTemplate.plain_text_body || ''}
                    onChange={(e) => setSelectedTemplate({
                      ...selectedTemplate,
                      plain_text_body: e.target.value
                    })}
                    helperText="Bruges når HTML ikke kan vises"
                  />
                )}
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Right: Variables */}
        <Box sx={{ flex: 1 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              📝 Variabler
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Klik for at kopiere, eller indsæt direkte i editoren
            </Typography>

            <Stack spacing={1}>
              {(selectedTemplate?.variables && selectedTemplate.variables.length > 0 
                ? selectedTemplate.variables 
                : DEFAULT_VARIABLES
              ).map((variable) => (
                <Paper
                  key={variable.key}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                    transition: 'all 0.2s',
                  }}
                  onClick={() => copyVariable(variable.key)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                        {`{{${variable.key}}}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {variable.label}
                      </Typography>
                      {variable.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                          {variable.description}
                        </Typography>
                      )}
                    </Box>
                    <Tooltip title="Kopier">
                      <IconButton size="small">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              ))}
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              💡 Tips
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              • Variabler erstattes automatisk ved afsendelse<br />
              • Brug Preview for at se resultatet<br />
              • Send en test email til dig selv først<br />
              • Plain text bruges som fallback
            </Typography>
          </Paper>
        </Box>
      </Box>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Email Preview</Typography>
            <ToggleButtonGroup
              value={previewDevice}
              exclusive
              onChange={(_, v) => v && setPreviewDevice(v)}
              size="small"
            >
              <ToggleButton value="desktop">
                <ComputerIcon />
              </ToggleButton>
              <ToggleButton value="mobile">
                <PhoneIphoneIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedTemplate && (
            <Box>
              {/* Subject preview */}
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.100' }}>
                <Typography variant="caption" color="text.secondary">
                  Emnefelt:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {replaceVariables(selectedTemplate.subject)}
                </Typography>
              </Paper>

              {/* HTML preview */}
              <Paper
                variant="outlined"
                sx={{
                  p: 0,
                  overflow: 'hidden',
                  mx: 'auto',
                  width: previewDevice === 'mobile' ? 375 : '100%',
                  transition: 'width 0.3s',
                }}
              >
                <Box
                  sx={{ 
                    bgcolor: 'white',
                    minHeight: 400,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: replaceVariables(selectedTemplate.html_body)
                  }}
                />
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Luk</Button>
        </DialogActions>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={testEmailOpen} onClose={() => setTestEmailOpen(false)}>
        <DialogTitle>Send Test Email</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Send en test email for at se hvordan den ser ud i din inbox.
          </Typography>
          <TextField
            fullWidth
            type="email"
            label="Din email adresse"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="din@email.dk"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestEmailOpen(false)}>Annuller</Button>
          <Button
            variant="contained"
            onClick={handleSendTestEmail}
            disabled={!testEmail || sendingTest}
            startIcon={sendingTest ? <CircularProgress size={20} /> : <SendIcon />}
          >
            Send Test
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

